import { getApiBaseUrl } from '@/lib/env';
import { getOrigin } from '@/lib/env';

/** Base URL for CMS features, handles fallback to admintest if env is missing. */
const getCmsBaseUrl = () => getApiBaseUrl();

export type SectionItemPayload = {
  id?: number;
  title?: string;
  subtitle?: string;
  description?: string;
  icon?: string;
  image?: string;
  button_text?: string;
  button_link?: string;
  metadata?: Record<string, unknown>;
  sort_order?: number;
};

export type PageSection = {
  type: string;
  key?: string;
  title?: string;
  content?: string;
  sort_order?: number;
  settings?: Record<string, unknown>;
  items?: SectionItemPayload[];
};

export type HeroBannerSettings = {
  title?: string;
  subtitle?: string;
  description?: string;
  image?: string;
  button_text?: string;
  button_link?: string;
  overlay?: boolean;
  alignment?: 'left' | 'center' | 'right';
};

export type Page = {
  id?: number;
  title: string;
  slug: string;
  content: string;
  meta_title?: string;
  meta_description?: string;
  status?: string;
  sections?: PageSection[];
};

export type Blog = {
  id: number;
  title: string;
  slug: string;
  excerpt?: string;
  content?: string;
  featured_image?: string;
  author?: { name: string } | string;
  author_id?: number;
  status?: string;
  published_at?: string;
  created_at?: string;
  category?: string;
};

export type Faq = {
  id: number;
  question: string;
  answer: string;
  sort_order?: number;
  status?: string;
};

export type SectionFeature = {
  icon?: string;
  title: string;
  description: string;
};

export type SectionPlan = {
  name: string;
  price: string;
  description: string;
  features: string[];
  cta: string;
  popular?: boolean;
};

export type SectionStat = {
  value: string;
  label: string;
};

export type SectionEventType = {
  name: string;
  count: string;
  color?: string;
};

export type SectionsPayload = {
  features?: SectionFeature[];
  plans?: SectionPlan[];
  stats?: SectionStat[];
  eventTypes?: SectionEventType[];
};

export type SettingsPayload = {
  platform_name?: string;
  support_email?: string;
  contact_email?: string;
  contact_phone?: string;
  contact_address?: string;
  footer_text?: string;
  footer_links?: Record<string, { label: string; href: string }[]>;
  social_links?: Record<string, string>;
  [key: string]: unknown;
};

let settingsCache: SettingsPayload | null = null;
let settingsRequestInFlight: Promise<SettingsPayload> | null = null;

export function clearSettingsCache(): void {
  settingsCache = null;
  settingsRequestInFlight = null;
}

async function fetchJson<T>(url: string, timeoutMs = 15000): Promise<T> {
  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), timeoutMs);
  const res = await fetch(url, {
    credentials: 'include',
    headers: { Accept: 'application/json' },
    signal: controller.signal,
  }).finally(() => clearTimeout(timeoutId));
  if (!res.ok) {
    throw new Error(`CMS fetch failed: ${res.status} ${url}`);
  }
  const data = await res.json();
  return (data?.data ?? data) as T;
}

export async function getPage(slug: string): Promise<Page | null> {
  try {
    const base = getCmsBaseUrl();
    return await fetchJson<Page>(`${base}/pages/${slug}`);
  } catch {
    return null;
  }
}

export async function getBlogs(): Promise<Blog[]> {
  try {
    const base = getCmsBaseUrl();
    const data = await fetchJson<{ data?: Blog[] } | Blog[]>(`${base}/blogs`);
    const list = Array.isArray(data) ? data : data?.data;
    return Array.isArray(list) ? list : [];
  } catch {
    return [];
  }
}

export async function getBlog(slug: string): Promise<Blog | null> {
  try {
    const base = getCmsBaseUrl();
    return await fetchJson<Blog>(`${base}/blogs/${slug}`);
  } catch {
    return null;
  }
}

export async function getFaqs(): Promise<Faq[]> {
  try {
    const base = getCmsBaseUrl();
    const data = await fetchJson<{ data?: Faq[] } | Faq[]>(`${base}/faqs`);
    const list = Array.isArray(data) ? data : data?.data;
    return Array.isArray(list) ? list : [];
  } catch {
    return [];
  }
}

export async function getSections(page: string): Promise<SectionsPayload> {
  try {
    const base = getCmsBaseUrl();
    return await fetchJson<SectionsPayload>(`${base}/sections/${page}`);
  } catch {
    return {};
  }
}

export async function getSettings(): Promise<SettingsPayload> {
  if (settingsCache) return settingsCache;
  if (settingsRequestInFlight) return settingsRequestInFlight;

  settingsRequestInFlight = (async () => {
    try {
      const base = getCmsBaseUrl();
      const data = await fetchJson<{ data?: SettingsPayload } | SettingsPayload>(`${base}/settings`, 10000);
      const resolved =
        data && typeof data === 'object' && !Array.isArray(data) && (data as any).data != null
          ? (data as any).data
          : (data as SettingsPayload) || {};
      settingsCache = resolved;
      return resolved;
    } catch {
      settingsCache = {};
      return {};
    } finally {
      settingsRequestInFlight = null;
    }
  })();

  return settingsRequestInFlight;
}

export type UiBootstrapMenuItem = {
  label: string;
  href: string;
  icon?: string;
  description?: string;
};

export type UiBootstrapPayload = {
  features?: Record<string, boolean>;
  menus?: Record<string, { items: UiBootstrapMenuItem[] }[]>;
};

export async function getUiBootstrap(): Promise<UiBootstrapPayload> {
  try {
    const base = getCmsBaseUrl();
    return await fetchJson<UiBootstrapPayload>(`${base}/ui/bootstrap`);
  } catch {
    return { features: {}, menus: {} };
  }
}

export async function trackUiUsageEvent(data: { event_type: string; button_key: string; metadata?: any }): Promise<void> {
  try {
    const base = getCmsBaseUrl();
    await fetch(`${base}/ui/usage-events`, {
      method: 'POST',
      credentials: 'include',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json',
      },
      body: JSON.stringify(data),
    });
  } catch {
    // Silent fail for tracking
  }
}
